// KPI card with embedded sparkline + delta arrow + status dot.

import { Link } from 'react-router-dom';
import { Sparkline } from '../charts/Sparkline.jsx';
import { useOptionalRichTooltipController } from '../richTooltips/RichTooltipProvider.jsx';

const TONE_BY_DELTA = (delta) =>
  delta == null ? 'slate' : delta > 0 ? 'sage' : delta < 0 ? 'brick' : 'slate';

export function KpiCard({
  id,
  surface = 'generic',
  label,
  value,
  unit,
  delta,
  deltaLabel,
  deltaTone,
  sparkline,
  sparklineTone,
  status,
  statusLabel,
  footer,
  icon,
  highlight,
  richTooltip = null,
  to = null,
}) {
  const tooltip = useOptionalRichTooltipController();
  const active = richTooltip && tooltip?.isActive(richTooltip.surfaceId);
  const pinned = richTooltip && tooltip?.isPinned(richTooltip.surfaceId);
  const triggerProps =
    richTooltip && tooltip
      ? tooltip.getTriggerProps({
          surfaceId: richTooltip.surfaceId,
          payload: richTooltip,
          testIdPrefix: 'rich-tooltip',
          ariaLabel: `${label}: ${value}`,
        })
      : {};
  const isLink = Boolean(to) && !richTooltip;
  const cls = `kpi-card ${highlight ? 'is-highlight' : ''} ${status ? `status-${status}` : ''}${richTooltip ? ' rich-tooltip-trigger-surface' : ''}${isLink ? ' is-link' : ''}${active ? ' is-active' : ''}${pinned ? ' is-pinned' : ''}`;
  const dTone = deltaTone || TONE_BY_DELTA(delta);
  const arrow = delta == null ? '' : delta > 0 ? '▲' : delta < 0 ? '▼' : '▬';
  // Opt-in navigation: a card with `to` renders as a real Link (keyboard and
  // screen-reader affordances for free). richTooltip mode keeps precedence.
  const Tag = richTooltip ? 'button' : isLink ? Link : 'article';
  return (
    <Tag
      type={richTooltip ? 'button' : undefined}
      to={isLink ? to : undefined}
      aria-label={isLink ? `${label}: ${value} ${unit || ''}`.trim() : undefined}
      className={cls}
      data-testid={id ? `kpi-card-${surface}-${id}` : undefined}
      {...triggerProps}
    >
      <header className="kpi-head">
        {icon ? (
          <span className="kpi-icon" aria-hidden>
            {icon}
          </span>
        ) : null}
        <span className="eyebrow">{label}</span>
        {statusLabel ? (
          <span className={`chip ${status || 'outline'} sm`}>
            <span className="chip-dot" />
            {statusLabel}
          </span>
        ) : null}
      </header>
      <div className="kpi-value">
        <span className="kpi-number">{value}</span>
        {unit ? <span className="kpi-unit">{unit}</span> : null}
      </div>
      {delta != null || deltaLabel ? (
        <div className={`kpi-delta tone-${dTone}`}>
          <span aria-hidden>{arrow}</span>
          <span>{deltaLabel ?? (delta > 0 ? `+${delta}` : `${delta}`)}</span>
        </div>
      ) : null}
      {sparkline?.length ? (
        <div className="kpi-spark">
          <Sparkline values={sparkline} width={180} height={36} tone={sparklineTone || 'blue'} />
        </div>
      ) : null}
      {footer ? <div className="kpi-footer">{footer}</div> : null}
    </Tag>
  );
}
